import { useState } from 'react'

import { usePermissions } from '@/hooks/usePermissions'

import {
  type IncidentOptionI,
  useTailgateMixpanelEvents,
} from '@/features/tailgate-grid'
import {
  IncidentQueryResult,
  useDoorTailgateGridsEnabledQuery,
} from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

/**
 * Centralises all Grid Testing Zone logic used by the Incident Drawer entry
 * point: eligibility gating, query for saved grids, preset incident building,
 * Mixpanel tracking, and open/close state.
 */
export const useGridTesting = (
  incident: IncidentQueryResult['data']['incident']
) => {
  const { shouldEnableGridTesting } = usePermissions()

  const device = incident?.devices[0]

  // The entry point is deliberately incident-type-agnostic (ENG-34040): it
  // serves both grid-testing flows, and the tailgate-evaluator vs
  // ingress-detector decision is owned entirely by GridTestingZone, which
  // derives it from the incident the test will run against. Keeping zero
  // routing logic here guarantees every entry point behaves identically.

  // Gate: PM/implementer + Door device + video clip + at least one saved grid.
  const isDoorIncidentWithVideo =
    device?.type === DeviceType.Door && Boolean(incident?.video?.url)
  const canViewGridSetup = shouldEnableGridTesting && isDoorIncidentWithVideo

  const { data: tailgateGridsData } = useDoorTailgateGridsEnabledQuery({
    skip: !canViewGridSetup || !device?.id,
    variables: { id: device?.id ?? '' },
  })

  // First saved grid on this door (ticket: "pick first if multiple").
  const firstTailgateGrid =
    tailgateGridsData?.device?.door?.tailgateGrids?.[0] ?? null
  const gridCameraId = firstTailgateGrid?.camera?.id ?? null
  const gridCameraName = firstTailgateGrid?.camera?.name ?? ''
  const isGridConfigured = !!firstTailgateGrid?.inBox

  const isEnabled = canViewGridSetup && Boolean(gridCameraId)

  const [isOpen, setIsOpen] = useState(false)
  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  const presetIncident: IncidentOptionI | null = incident
    ? {
        id: incident.id,
        incidentId: incident.displayId,
        label: incident.displayId,
        createdAt: incident.createdAt,
        status: incident.status ?? undefined,
        type: incident.type ?? undefined,
        incidentTypeSlug: incident.incidentType?.slug ?? undefined,
      }
    : null

  const { trackTailgateEvent } = useTailgateMixpanelEvents({
    facilityId: incident?.facility?.id ?? '',
    floorId: incident?.floor?.id ?? '',
    cameraId: gridCameraId ?? '',
    doorId: device?.id ?? '',
  })

  return {
    isGridConfigured,
    isEnabled,
    isOpen,
    open,
    close,
    presetIncident,
    gridCameraId,
    gridCameraName,
    device,
    trackTailgateEvent,
  }
}
